import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Section, { Reveal } from '../components/Section';
import ContactForm from '../components/ContactForm';

/**
 * Help hub — one page a confused user can land on and self-serve from.
 * Search across the FAQ + quick-fix cards, category tiles that deep-link
 * into /docs, contact form at the bottom for what can't be solved by a
 * doc, and clear escalation paths for billing / security / abuse.
 */

type Faq = { q: string; a: JSX.Element; tags: string; category: string };

const FAQ: Faq[] = [
  { category: 'Connect', tags: 'connect handshake fail timeout dpi reality',
    q: 'The app says "Connected" but nothing loads.',
    a: <>Ninety percent of the time this is an MTU issue. Settings → Advanced → MTU → drop to <strong>1280</strong>. Full explanation on <Link to="/docs/mtu-sizing">the MTU page</Link>.</> },
  { category: 'Connect', tags: 'handshake reality tls dpi fragmentation',
    q: 'REALITY handshake keeps timing out.',
    a: <>Flip <strong>Anti-DPI / TLS fragmentation</strong> in Settings → Advanced, then reconnect. If it still fails, pick a different city manually (Pro) and retry. Full guide on <Link to="/docs/connect-fails">Connect fails</Link>.</> },
  { category: 'Speed', tags: 'slow speed test throughput mtu',
    q: 'My speed dropped a lot after enabling Vpnee.',
    a: <>Baseline first — run <a href="https://speed.cloudflare.com" target="_blank" rel="noopener">speed.cloudflare.com</a> with the tunnel on then off. Vpnee typically hits 60–85% of raw speed. Under 30% usually means MTU or a saturated node; try a different city or drop MTU.</> },
  { category: 'Speed', tags: 'buffering streaming netflix youtube',
    q: 'Streaming buffers at high resolution.',
    a: <>Pick a geographically closer city (Pro) and confirm the server pool shows &lt;60ms ping. Post-quantum crypto has zero overhead per-packet, so this is almost always propagation latency — not the tunnel.</> },
  { category: 'Account', tags: 'login password reset forgot email',
    q: 'I forgot my password.',
    a: <>Sign-in screen → <strong>Forgot?</strong>. Reset link goes to your account email and expires in 15 minutes. No email on file? Use Google / Facebook sign-in with the same email address instead — accounts are matched by email.</> },
  { category: 'Account', tags: 'signed out logged out session expired',
    q: 'I keep getting signed out.',
    a: <>Session tokens expire after 30 days of inactivity, or immediately if you revoked the device from another session. Sign in again; if it happens repeatedly, check Account → Devices for revocations you did not make and change your password.</> },
  { category: 'Account', tags: 'delete account gdpr close remove',
    q: 'How do I delete my account?',
    a: <>Account → Security → <strong>Delete account</strong>. Scheduled for removal in 7 days; sign in again during that window to cancel. Active pro plans must be cancelled first.</> },
  { category: 'Billing', tags: 'refund money back charge cancel',
    q: 'I want a refund.',
    a: <>Within 7 days of purchase and under 5% of the plan's monthly data consumed, email <a href="mailto:billing@tnidu.com">billing@tnidu.com</a> — auto-approved. Outside that window, case-by-case. Full policy on <Link to="/docs/refunds">Refunds</Link>.</> },
  { category: 'Billing', tags: 'coupon code discount not working',
    q: 'My coupon code says "invalid".',
    a: <>Codes are case-insensitive but strict about whitespace. Paste again with no leading/trailing space. Free-days coupons only work on accounts that don't currently have Pro. Discount coupons stack with annual pricing but not with a live global promo (the larger of the two wins).</> },
  { category: 'Billing', tags: 'card payment failed decline',
    q: 'My card was declined.',
    a: <>Try again, then try a different card. Most declines are 3-D Secure — your bank's SMS took &gt;90 s to arrive. If it fails twice, use Alipay or email <a href="mailto:billing@tnidu.com">billing@tnidu.com</a>.</> },
  { category: 'Privacy', tags: 'logs privacy zero log dns leak',
    q: 'How do I confirm you actually keep zero logs?',
    a: <>You can't blindly trust us — verify from the wire. See <Link to="/docs/reality-explained">REALITY explained</Link> for a Wireshark walkthrough, and <Link to="/docs/kill-switch">Kill switch & leaks</Link> for DNS-leak tests. We also publish transparency reports twice a year (zero requests to date).</> },
  { category: 'Devices', tags: 'multiple devices slot android windows',
    q: 'Can I use one account on multiple devices?',
    a: <>Yes. Free: 1 device. Gold: 3. Platinum: 5. Exceeding the slot signs the oldest inactive device out automatically. See <Link to="/docs/device-management">Devices & sessions</Link>.</> },
  { category: 'Devices', tags: 'ios iphone testflight beta',
    q: 'When is the iOS app out?',
    a: <>Public TestFlight opens in the next release. The link goes up on <Link to="/download">the download page</Link> — bookmark it or drop us an email at <a href="mailto:hello@tnidu.com">hello@tnidu.com</a> and we'll ping you.</> },
  { category: 'Devices', tags: 'linux appimage deb rpm',
    q: 'Linux support?',
    a: <>AppImage, .deb, and .rpm preview builds are on the download page. Full details on <Link to="/docs/linux-preview">Linux (preview)</Link>.</> },
];

const svgProps = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const QUICK_FIX: { icon: JSX.Element; title: string; body: string; to: string }[] = [
  { icon: <svg {...svgProps} aria-hidden><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    title: 'App running, no internet',   body: 'MTU is the usual cause. Fix in 30 seconds.',                       to: '/docs/mtu-sizing' },
  { icon: <svg {...svgProps} aria-hidden><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14"/></svg>,
    title: 'Tunnel drops repeatedly',    body: 'Aggressive keepalive plus a stronger network path.',                to: '/docs/troubleshooting' },
  { icon: <svg {...svgProps} aria-hidden><path d="M5 12.55a11 11 0 0 1 14 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>,
    title: 'Slow speed on Wi-Fi / cellular', body: 'Where the bottleneck actually is, and how to test.',            to: '/docs/troubleshooting' },
  { icon: <svg {...svgProps} aria-hidden><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>,
    title: 'DNS-leak / IP-leak test',    body: 'Verify from the wire — kill switch, DNS resolver, IPv6 policy.',    to: '/docs/kill-switch' },
  { icon: <svg {...svgProps} aria-hidden><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    title: 'Reach a human on support',   body: 'Contact form, email lanes, expected turnaround.',                   to: '/help#contact' },
  { icon: <svg {...svgProps} aria-hidden><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="14" x2="10" y2="14"/></svg>,
    title: 'Refund / coupon / billing',  body: 'Full policy in plain English.',                                     to: '/docs/refunds' },
];

const CATEGORIES: { title: string; body: string; to: string; icon: JSX.Element }[] = [
  { title: 'Getting started', body: 'Install, sign up, first connect, verify.',
    to: '/docs/getting-started',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polygon points="5 3 19 12 5 21 5 3"/></svg> },
  { title: 'Networking & routing', body: 'Servers, MTU, split tunneling, kill switch.',
    to: '/docs/servers',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> },
  { title: 'Platform features', body: 'Widgets, tiles, notifications, watchdog.',
    to: '/docs/quick-connect-shortcuts',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
  { title: 'Privacy & security', body: 'Zero-log, post-quantum, devices & sessions.',
    to: '/docs/privacy-notes',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
  { title: 'Billing & plans', body: 'Coupons, data allowance, refunds.',
    to: '/docs/coupons',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg> },
  { title: 'Troubleshooting', body: 'When things break — narrow it down fast.',
    to: '/docs/troubleshooting',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg> },
];

export default function Help() {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return FAQ;
    return FAQ.filter(f => (f.q + ' ' + f.tags + ' ' + f.category).toLowerCase().includes(needle));
  }, [q]);

  const byCategory = useMemo(() => {
    const m = new Map<string, Faq[]>();
    filtered.forEach(f => { const a = m.get(f.category) || []; a.push(f); m.set(f.category, a); });
    return [...m.entries()];
  }, [filtered]);

  return (
    <>
      {/* Hero — big search */}
      <section className="relative overflow-hidden pt-24 pb-10 sm:pt-32 sm:pb-16">
        <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg" />
        <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[color:var(--color-brand)]/22 blur-[110px]" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-32 w-[380px] h-[380px] rounded-full bg-[color:var(--color-brand-deep)]/22 blur-[110px]" />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-5 text-center">
          <div className="chip mb-5 inline-flex">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand-glow)] animate-pulse" />
            Help & support
          </div>
          <h1 className="h-title text-[2.2rem] sm:text-5xl md:text-6xl font-extrabold leading-[1.05]">
            How can we help?
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[color:var(--color-ink-dim)]">
            Search the FAQ, jump to the exact doc you need, or drop us a note. Real humans on the other end.
          </p>

          <div className="mt-8 relative">
            <input
              type="search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search FAQ — e.g. slow, refund, coupon, MTU…"
              aria-label="Search help"
              className="w-full h-14 rounded-2xl bg-black/40 border border-white/10 backdrop-blur-xl pl-14 pr-4 text-base outline-none focus:border-[color:var(--color-brand)] focus:ring-2 focus:ring-[color:var(--color-brand)]/25 transition"
            />
            <svg aria-hidden width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-5 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-faint)]"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
            {['MTU', 'refund', 'slow speed', 'coupon', 'Android', 'Windows', 'delete account'].map(term => (
              <button key={term} onClick={() => setQ(term)} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[color:var(--color-ink-dim)] hover:text-white hover:border-[color:var(--color-brand)]/40 transition">
                {term}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Quick fixes strip */}
      <Section chip="Quick fixes" title="Most common in 30 seconds." subtitle="If your question is one of these, jump straight to the doc — you won't need us.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {QUICK_FIX.map((qf, i) => (
            <Reveal key={qf.title} delay={i * 0.04}>
              <Link to={qf.to} className="glass p-6 h-full block hover:border-[color:var(--color-brand)]/40 transition group">
                <div className="w-11 h-11 rounded-xl grid place-items-center bg-[color:var(--color-brand)]/10 border border-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)] mb-4">{qf.icon}</div>
                <div className="font-[family-name:var(--font-display)] font-bold text-lg mb-1.5">{qf.title}</div>
                <div className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{qf.body}</div>
                <div className="mt-4 text-xs text-[color:var(--color-brand-glow)] font-semibold uppercase tracking-widest inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  Open guide <span aria-hidden>→</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Category tiles */}
      <Section chip="Browse by category" title="All the docs, sorted.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.04}>
              <Link to={c.to} className="glass p-6 h-full flex items-start gap-4 hover:border-[color:var(--color-brand)]/40 transition">
                <div className="w-11 h-11 rounded-xl grid place-items-center bg-[color:var(--color-brand)]/10 border border-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)] shrink-0">
                  {c.icon}
                </div>
                <div className="min-w-0">
                  <div className="font-[family-name:var(--font-display)] font-bold text-lg mb-1">{c.title}</div>
                  <div className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{c.body}</div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FAQ (filtered) */}
      <Section chip="FAQ" title={q ? `Results for "${q}"` : 'Frequently asked'}>
        {filtered.length === 0 ? (
          <div className="glass p-8 text-center">
            <div className="mx-auto mb-3 w-14 h-14 rounded-2xl grid place-items-center bg-[color:var(--color-brand)]/10 border border-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)]" aria-hidden>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            </div>
            <div className="font-[family-name:var(--font-display)] font-bold text-xl mb-1.5">No FAQ matches "{q}"</div>
            <div className="text-sm text-[color:var(--color-ink-dim)]">Try one of the quick-fix tiles above, or scroll down to write us directly.</div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {byCategory.map(([cat, items]) => (
              <div key={cat} className="glass p-6">
                <div className="chip mb-4">{cat}</div>
                <div className="space-y-3">
                  {items.map(f => (
                    <details key={f.q} className="rounded-xl border border-white/10 bg-black/20 p-4 open:pb-5 group">
                      <summary className="cursor-pointer list-none flex justify-between items-start gap-3 font-semibold text-sm">
                        <span>{f.q}</span>
                        <span className="text-[color:var(--color-brand-glow)] transition-transform group-open:rotate-45 text-xl leading-none shrink-0">+</span>
                      </summary>
                      <div className="mt-2.5 text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{f.a}</div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Escalation lanes */}
      <Section chip="Reach a human" title="Where to write us.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { t: 'General', e: 'hello@tnidu.com',    note: 'Anything that doesn\'t fit the others. Weekdays: reply in a few hours.' },
            { t: 'Billing', e: 'billing@tnidu.com',  note: 'Refunds, coupons, invoices, disputes.' },
            { t: 'Security', e: 'security@tnidu.com', note: 'Vulnerability reports, suspected account takeover, disclosure requests.' },
            { t: 'Abuse', e: 'abuse@tnidu.com',      note: 'DMCA, spam originating from a Vpnee IP, court orders. Copied to privacy@tnidu.com for audit trail.' },
            { t: 'Legal notices', e: 'legal@tnidu.com', note: 'Formal legal notices, service-of-process, corporate correspondence. Copied to privacy@tnidu.com for audit trail.' },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 0.04}>
              <div className="glass p-6 h-full flex flex-col">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--color-ink-faint)] mb-1">{x.t}</div>
                <a href={`mailto:${x.e}`} className="font-[family-name:var(--font-display)] font-bold text-lg text-[color:var(--color-brand-glow)] break-all">{x.e}</a>
                <div className="mt-2 text-sm text-[color:var(--color-ink-dim)] leading-relaxed flex-1">{x.note}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Contact form */}
      <Section id="contact" chip="Direct message" title="Or write it here." subtitle="Goes to the same inbox as the email lanes above. Include your username if you already have an account — we can pull up your session in a click.">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
          <div>
            <ContactForm />
          </div>
          <Reveal>
            <div className="glass p-6 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] mb-1.5">Turnaround</div>
                <div className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Weekdays 08:00–20:00 <abbr title="Sri Lanka Standard Time">SLST</abbr>: reply in a few hours. Weekends: within 24 h. If it's blocking you, add <strong>URGENT</strong> to the subject.</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] mb-1.5">What to include</div>
                <ul className="text-sm text-[color:var(--color-ink-dim)] list-disc pl-5 space-y-1">
                  <li>Your username or the email you signed up with</li>
                  <li>App version — Settings → About → tap the version 3× to copy diagnostics</li>
                  <li>Approximate time the issue started (with timezone)</li>
                  <li>Screenshot / short screen recording if it's a UI issue</li>
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] mb-1.5">Status page</div>
                <div className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Current node health and incidents live on <a className="text-[color:var(--color-brand-glow)] underline" href="https://status.tnidu.com" target="_blank" rel="noopener">status.tnidu.com</a>.</div>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
